import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input, NativeSelect } from '@/components/ui/Input';
import { Checkbox } from '@/components/ui/Checkbox';
import { Badge } from '@/components/ui/Badge';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/Table';
import { LoadingState, ErrorState } from '@/components/ui/States';
import { ApiError } from '@/lib/api/client';
import VoiceMicButton from '@/components/VoiceMicButton';
import VoiceFormFiller, { type VoiceField } from '@/components/VoiceFormFiller';
import { loadConfig, getActiveModel, type AIMessage } from '@/aiConfig';
import { authFetch, saveConsultation, getAuthUser } from '@/api';
import DocumentPreviewButtons from '@/components/DocumentPreviewButtons';
import { useAiAvailability } from '@/features/ai-assist/useAiAvailability';
import { cleanAIDraft } from '@/lib/aiDraftCleanup';
import DocumentEditor, { type InsertableValue } from '@/features/ai-assist/DocumentEditor';
import type { HeadingSection } from '@/features/ai-assist/docAssist';
import { spliceSectionFromReply } from '@/features/ai-assist/documentSections';
import { checkPrescriptionSheet, getPrescriptionSheet, type SheetComparison } from '@/features/ai-assist/api';
import { generateDocument, type AppliedMemoryItem } from '@/features/ai-assist/streamGeneration';
import { toast } from '@/hooks/useToast';
import { formatDocDate, removeAllPlaceholders, unfilledFieldLabels } from '@/features/ai-assist/smartFields';
import { PersonalisedChip } from '@/features/ai-assist/PersonalisedChip';
import { MyAiPreferencesPanel } from '@/features/ai-assist/MyAiPreferencesPanel';
import { getNextProposal, type DoctorPreference } from '@/features/ai-assist/memoryApi';
import { ProposalCard } from '@/features/ai-assist/ProposalCard';
import { useVisitPrescriptions, useCreatePrescription, useSavePrescription, useSetPrescriptionStatus } from './usePrescriptionDraft';
import { usePatientPrescriptions } from '@/features/prescriptions/usePatientPrescriptions';
import { useVisitDiagnoses } from './useEncounterDiagnoses';
import { getPrescriptionVersioned, searchMedicines, fetchBrandsForGeneric, getFormularyStatus, checkPrescriptionSafety, recordPrescriptionAiProposals } from './clinicalApi';
import { formatItemsAsRxLines, reconcileAiPrescriptionLines } from './prescriptionAiReconcile';
import type { PrescriptionItemInput, PrescriptionDurationUnit, PrescriptionStatus, MedicineSearchResult, MedicineBrandResult, FormularyStatusResponse, SafetyAlert } from './types';
import { deriveQuantity } from './prescriptionQuantity';
import SafetyAlertBanner from '@/components/SafetyAlertBanner';
import { PLAN_HANDOFF_EVENT, type PlanHandoffDetail } from './planHandoff';

const DURATION_UNITS: PrescriptionDurationUnit[] = ['d', 'wk', 'mo'];
// Standard Indian prescribing shorthand — not exhaustive (frequency_text
// carries anything this list can't express, per prescription_items' schema
// comment).
const FREQUENCY_CODES = ['OD', 'BD', 'TDS', 'QID', 'HS', 'SOS', 'STAT'];

// One dictation covering a whole medicine line ("Paracetamol 650mg, one
// tablet, oral, three times a day for five days") rather than per-cell
// dictation — matches how a prescription is actually spoken aloud.
const ITEM_VOICE_FIELDS: VoiceField[] = [
  { key: 'drug_name', label: 'Drug name', type: 'text' },
  { key: 'strength', label: 'Strength', type: 'text' },
  { key: 'dose_quantity', label: 'Dose quantity', type: 'number' },
  { key: 'dose_unit', label: 'Dose unit', type: 'text' },
  { key: 'route', label: 'Route', type: 'text' },
  { key: 'frequency_code', label: 'Frequency code', type: 'text', options: FREQUENCY_CODES },
  { key: 'frequency_text', label: 'Frequency notes', type: 'text' },
  { key: 'duration_value', label: 'Duration value', type: 'number' },
  { key: 'duration_unit', label: 'Duration unit', type: 'text', options: DURATION_UNITS },
  { key: 'instructions', label: 'Instructions', type: 'text' },
];

function emptyItem(sequence: number): PrescriptionItemInput {
  return { drug_name: '', substitution_allowed: true, sequence };
}

// Parses the AI's "Rx. <drug> <strength> — <dose>, <route>, <freq> × <duration>"
// lines (the same "Rx." convention ConsultationChat's drafts and
// cleanAIDraft already key off) into draft table rows. Best-effort: the
// table stays fully editable afterward, so a line the AI didn't format
// exactly right just means the doctor fixes that one cell rather than the
// draft being rejected outright.
function parseAiPrescriptionLines(text: string, startSequence: number): PrescriptionItemInput[] {
  const lines = text.split('\n').map(l => l.trim()).filter(l => /^Rx\.\s*\S/.test(l));
  return lines.map((line, i) => {
    const rest = line.replace(/^Rx\.\s*/, '');
    // Models are inconsistent about which dash they use for the separator
    // ("—" em, "–" en, or plain "-") despite the instruction specifying one.
    const splitRest = rest.split(/\s+[-–—]\s+/);
    const namePart = splitRest[0] ?? rest;
    const rxPart = splitRest[1] ?? '';
    const nameMatch = namePart.match(/^(.*?)\s*([\d.]+\s?(?:mg|g|mcg|ml|iu|%))\s*$/i);
    const drug_name = (nameMatch?.[1] ?? namePart).trim();
    const strength = nameMatch?.[2]?.trim();

    const [doseSeg, routeSeg, freqDurSeg] = rxPart.split(',').map(s => s.trim());
    const doseMatch = doseSeg?.match(/^([\d.]+)\s*(\S.*)?$/);
    const freqMatch = freqDurSeg?.match(/^([A-Za-z]+)?\s*(?:[×x]\s*([\d.]+)\s*(d|wk|mo))?/i);
    const freqWord = freqMatch?.[1];
    const isCode = freqWord && FREQUENCY_CODES.includes(freqWord.toUpperCase() as typeof FREQUENCY_CODES[number]);

    const item = emptyItem(startSequence + i);
    item.drug_name = drug_name || rest;
    if (strength) item.strength = strength;
    if (doseMatch) {
      item.dose_quantity = Number(doseMatch[1]) || null;
      if (doseMatch[2]) item.dose_unit = doseMatch[2];
    }
    if (routeSeg) item.route = routeSeg;
    if (isCode) item.frequency_code = freqWord!.toUpperCase();
    else if (freqDurSeg) item.frequency_text = freqDurSeg;
    if (freqMatch?.[2]) item.duration_value = Number(freqMatch[2]) || null;
    if (freqMatch?.[3]) item.duration_unit = freqMatch[3].toLowerCase() as PrescriptionDurationUnit;
    item.quantity = deriveQuantity(item);
    return item;
  });
}

// Fallback for tenants whose active "OPD Prescription" AI Assistant Role
// profile (Admin panel → AI role, maker-checker approved) mandates a
// Markdown medication table instead of "Rx." lines — profiles are
// hospital-authored free text, so the model follows whatever format that
// profile's own system prompt demands, "Rx." or not. Column-name-driven
// (not positional) so it tolerates whatever subset/order of the usual
// Medicine/Brand/Strength/Dose/Route/Frequency/Duration/Instructions
// columns a given profile's example table happens to show.
// Exported (not just used internally) so the v3 Consultation layout's Plan
// table hand-off (planHandoff.ts) reuses this exact parser instead of a
// second, possibly-drifting implementation of the same table shape.
export function parseAiPrescriptionTable(text: string, startSequence: number): PrescriptionItemInput[] {
  const rows = text.split('\n').map(l => l.trim()).filter(l => /^\|.*\|$/.test(l));
  if (rows.length < 2) return [];
  const splitRow = (row: string) => row.slice(1, -1).split('|').map(c => c.trim());
  const isSeparatorRow = (cells: string[]) => cells.every(c => /^:?-+:?$/.test(c));

  const headerIdx = rows.findIndex((_, i) => rows[i + 1] != null && isSeparatorRow(splitRow(rows[i + 1]!)));
  if (headerIdx === -1) return [];
  const headers = splitRow(rows[headerIdx]!).map(h => h.toLowerCase());
  const col = (...names: string[]) => headers.findIndex(h => names.some(n => h.includes(n)));
  const idx = {
    drug: col('medicine', 'drug'), brand: col('brand'), strength: col('strength'),
    dose: col('dose'), route: col('route'), freq: col('frequency'),
    duration: col('duration'), instructions: col('instruction'),
  };
  if (idx.drug === -1) return [];

  const dataRows = rows.slice(headerIdx + 2).filter(r => !isSeparatorRow(splitRow(r)));
  return dataRows.map((row, i) => {
    const cells = splitRow(row);
    const cell = (n: number) => (n === -1 ? '' : (cells[n] ?? '').trim());
    const item = emptyItem(startSequence + i);
    item.drug_name = cell(idx.drug);
    if (cell(idx.brand)) item.brand_name = cell(idx.brand);
    if (cell(idx.strength)) item.strength = cell(idx.strength);

    const doseMatch = cell(idx.dose).match(/^([\d.]+)\s*(\S.*)?$/);
    if (doseMatch) {
      item.dose_quantity = Number(doseMatch[1]) || null;
      if (doseMatch[2]) item.dose_unit = doseMatch[2];
    }
    if (cell(idx.route)) item.route = cell(idx.route);

    const freqCell = cell(idx.freq);
    if (freqCell) {
      if (FREQUENCY_CODES.includes(freqCell.toUpperCase() as typeof FREQUENCY_CODES[number])) {
        item.frequency_code = freqCell.toUpperCase();
      } else {
        item.frequency_text = freqCell;
      }
    }
    const durMatch = cell(idx.duration).match(/^([\d.]+)\s*(d|wk|mo)?/i);
    if (durMatch) {
      item.duration_value = Number(durMatch[1]) || null;
      if (durMatch[2]) item.duration_unit = durMatch[2].toLowerCase() as PrescriptionDurationUnit;
    }
    if (cell(idx.instructions)) item.instructions = cell(idx.instructions);
    item.quantity = deriveQuantity(item);
    return item;
  }).filter(it => it.drug_name.trim());
}

function itemFromVoiceFields(extracted: Record<string, string>, sequence: number): PrescriptionItemInput {
  const item = emptyItem(sequence);
  if (extracted.drug_name) item.drug_name = extracted.drug_name;
  if (extracted.strength) item.strength = extracted.strength;
  if (extracted.dose_quantity) item.dose_quantity = Number(extracted.dose_quantity) || null;
  if (extracted.dose_unit) item.dose_unit = extracted.dose_unit;
  if (extracted.route) item.route = extracted.route;
  if (extracted.frequency_code && (FREQUENCY_CODES as string[]).includes(extracted.frequency_code)) item.frequency_code = extracted.frequency_code;
  if (extracted.frequency_text) item.frequency_text = extracted.frequency_text;
  if (extracted.duration_value) item.duration_value = Number(extracted.duration_value) || null;
  if (extracted.duration_unit && (DURATION_UNITS as string[]).includes(extracted.duration_unit)) item.duration_unit = extracted.duration_unit as PrescriptionDurationUnit;
  if (extracted.instructions) item.instructions = extracted.instructions;
  item.quantity = deriveQuantity(item);
  return item;
}

interface PrescriptionBuilderProps {
  patientId: number;
  visitId: number;
  uhid?: string;
  /** Visit business code (VIS-...) — lets the AI draft ground itself in this
   * visit's own diagnoses (via useVisitDiagnoses) rather than working from
   * chief complaints alone. Omit to skip that lookup (e.g. no tracked visit). */
  visitCode?: string;
  /** Vitals/chief-complaints/pre-consult summary the AI draft is grounded in
   * — the same context block ConsultStage already assembles for the
   * narrative chat, reused here instead of asking the doctor to retype it. */
  aiClinicalContext?: string;
  /** For the "Generate prescription sheet" document header — the structured
   * medicine table doesn't need these (it's rendered from `items` directly),
   * but the AI-drafted narrative around it does, since neither `patientId`
   * nor `aiClinicalContext` names the patient. */
  patientName?: string;
  patientAge?: number | null;
  patientGender?: string;
  /** Recorded vitals (bp_systolic, bp_diastolic, pulse_rate, temperature,
   * spo2, weight_kg, bmi, ...) — same shape ConsultationChat's queueItem
   * already sends as its `vitals` param. Without this the backend's system
   * prompt reads every vital as unrecorded, which a cautious model (and a
   * strict AI Assistant Role profile) can read as reason enough to decline
   * drafting rather than propose lines for the doctor to review. */
  vitals?: Record<string, unknown>;
}

/**
 * Positions a search dropdown outside the prescription Table's own
 * `overflow-auto` wrapper (Table.tsx) by portaling it to document.body with
 * `position: fixed` coordinates read off the anchor field. An absolutely
 * positioned dropdown confined to that wrapper gets clipped, forcing the
 * doctor to scroll the whole table to see suggestions below the fold —
 * simply asking the wrapper for `overflow-y: visible` doesn't work either,
 * because the CSS Overflow spec force-computes a `visible` axis back to
 * `auto` whenever the other axis isn't `visible`, and the wrapper needs
 * `overflow-x: auto` for the table's own horizontal scroll. Recomputes on
 * scroll/resize while open so the dropdown tracks the field.
 */
function useAnchoredRect(anchorRef: React.RefObject<HTMLElement | null>, open: boolean) {
  const [rect, setRect] = useState<{ top: number; left: number; width: number } | null>(null);
  useEffect(() => {
    if (!open || !anchorRef.current) { setRect(null); return; }
    const update = () => {
      if (!anchorRef.current) return;
      const r = anchorRef.current.getBoundingClientRect();
      setRect({ top: r.bottom + 4, left: r.left, width: r.width });
    };
    update();
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [open, anchorRef]);
  return rect;
}

/**
 * Arrow-key navigation for a search dropdown — Down/Up move a highlighted
 * index, Enter picks it, Escape closes without picking. Purely keyboard
 * state; the caller still renders the highlight and still owns the actual
 * open/closed and item-list state. Resets the highlight whenever the item
 * list itself changes (a new keystroke's results landing, or the dropdown
 * closing) so a stale index from the previous result set is never carried
 * forward and silently selects the wrong drug/brand.
 */
function useDropdownKeyNav<T>(items: T[], open: boolean, onSelect: (item: T) => void, onClose: () => void) {
  const [activeIndex, setActiveIndex] = useState(-1);
  useEffect(() => { setActiveIndex(-1); }, [items, open]);
  const onKeyDown = (e: React.KeyboardEvent) => {
    if (!open || items.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex(i => (i + 1) % items.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex(i => (i <= 0 ? items.length - 1 : i - 1));
    } else if (e.key === 'Enter') {
      const item = items[activeIndex];
      if (item !== undefined) {
        e.preventDefault();
        onSelect(item);
      }
    } else if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
    }
  };
  return { activeIndex, onKeyDown };
}

/**
 * Drug-name cell with a search-as-you-type dropdown against the pharmacy's
 * medicines master, restricted to generic_name (GET /api/medicines's
 * field=generic mode — one row per distinct drug, not one per stocked
 * brand/strength combo). This tenant's medicines master only ever contains
 * what's been stocked or prescribed here before, never a complete national
 * formulary (see the module's own header note on where that would come
 * from), so the field stays fully free-text either way — a doctor must be
 * able to write a drug that isn't in it yet. Picking a suggestion clears
 * whatever brand/medicine_id was previously on this line, since a brand
 * chosen for a different drug no longer applies.
 */
function DrugNameCell({ value, onChange, onPick }: {
  value: string;
  onChange: (v: string) => void;
  onPick: (med: MedicineSearchResult) => void;
}) {
  const [results, setResults] = useState<MedicineSearchResult[]>([]);
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLDivElement>(null);
  const rect = useAnchoredRect(anchorRef, open && results.length > 0);
  const { activeIndex, onKeyDown } = useDropdownKeyNav(results, open, m => { onPick(m); setOpen(false); }, () => setOpen(false));

  useEffect(() => {
    if (!open || !value.trim()) { setResults([]); return; }
    const handle = setTimeout(() => {
      searchMedicines(value, { field: 'generic' }).then(setResults).catch(() => setResults([]));
    }, 250);
    return () => clearTimeout(handle);
  }, [value, open]);

  return (
    <div className="relative" ref={anchorRef}>
      <div className="flex items-center gap-1">
        <Input
          value={value}
          onChange={e => { onChange(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
          onKeyDown={onKeyDown}
          placeholder="Search drug name…"
          className="min-w-[140px]"
        />
        <VoiceMicButton onText={t => onChange(t)} title="Dictate drug name" />
      </div>
      {rect && open && results.length > 0 && createPortal(
        <div
          className="fixed z-50 max-h-48 overflow-y-auto rounded-[var(--radius)] border border-border bg-surface shadow-md"
          style={{ top: rect.top, left: rect.left, width: Math.max(rect.width, 224) }}
        >
          {results.map((m, idx) => (
            <button
              key={m.id}
              type="button"
              className={`block w-full px-2 py-1.5 text-left text-[12px] hover:bg-muted/60 ${idx === activeIndex ? 'bg-muted/60' : ''}`}
              onMouseDown={e => { e.preventDefault(); onPick(m); setOpen(false); }}
            >
              <div className="font-medium">{m.generic_name}</div>
            </button>
          ))}
        </div>,
        document.body
      )}
    </div>
  );
}

/**
 * Advisory-only formulary status badge (§9, §23 of the Drug Master
 * architecture doc) — fetched once per resolved drug, never on every
 * keystroke. Renders nothing at all for the common case (no
 * hospital_formulary row = allowed/unflagged) to avoid cluttering every
 * line with a badge that says nothing interesting. Never disables or
 * blocks anything on this row — restricted/non_formulary is shown purely
 * as context for the doctor to weigh.
 */
function FormularyBadge({ drugId }: { drugId: number | null | undefined }) {
  const [status, setStatus] = useState<FormularyStatusResponse['formulary']>(null);

  useEffect(() => {
    if (!drugId) { setStatus(null); return; }
    let cancelled = false;
    getFormularyStatus(drugId).then(r => { if (!cancelled) setStatus(r.formulary); }).catch(() => { if (!cancelled) setStatus(null); });
    return () => { cancelled = true; };
  }, [drugId]);

  if (!status || status.status === 'allowed') return null;
  const meta = {
    preferred: { label: '⭐ Preferred', className: 'text-success' },
    restricted: { label: '⚠ Restricted', className: 'text-warning' },
    non_formulary: { label: '⚠ Non-formulary', className: 'text-warning' },
  }[status.status];
  return (
    <div className={`mt-0.5 text-[10px] font-medium ${meta.className}`} title={status.prescribing_restriction || undefined}>
      {meta.label}
    </div>
  );
}

/**
 * Brand-name cell. Once a drug has been chosen (genericName non-empty),
 * suggestions are the brands actually known FOR THAT DRUG (GET
 * /api/medicines/brands), fetched once per drug rather than per keystroke
 * and filtered client-side as the doctor types, with brands this branch
 * currently stocks (pharmacy_inventory) sorted first and marked "✓ In
 * stock". Before a drug is chosen, falls back to the old brand-inclusive
 * free search (GET /api/medicines) — Indian prescribing is often
 * brand-name-led, so the doctor may type the brand before the generic is
 * known at all. Picking a suggestion fills brand name and — only when still
 * blank, never overwriting what the doctor already typed — the generic drug
 * name and strength too. The field stays a plain free-text input in every
 * case: a brand not in the master, or not currently stocked here, is still
 * a legitimate answer (the pharmacy might reorder it, or the patient buys
 * it elsewhere) — never a hard restriction.
 */
function BrandNameCell({ value, genericName, drugId, onPick, onChange }: {
  value: string;
  genericName: string;
  drugId?: number | null;
  onChange: (v: string) => void;
  onPick: (med: MedicineSearchResult) => void;
}) {
  const [results, setResults] = useState<MedicineSearchResult[]>([]);
  const [brandOptions, setBrandOptions] = useState<MedicineBrandResult[]>([]);
  const [open, setOpen] = useState(false);
  const scoped = genericName.trim().length > 0;
  const anchorRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!scoped) { setBrandOptions([]); return; }
    // drugId, once resolved, gives an exact match instead of the
    // generic-name ILIKE fallback — see fetchBrandsForGeneric's own
    // comment and design doc §21 (implementation plan item 11).
    fetchBrandsForGeneric(genericName, drugId).then(setBrandOptions).catch(() => setBrandOptions([]));
  }, [scoped, genericName, drugId]);

  useEffect(() => {
    if (scoped || !open || !value.trim()) { setResults([]); return; }
    const handle = setTimeout(() => {
      searchMedicines(value).then(setResults).catch(() => setResults([]));
    }, 250);
    return () => clearTimeout(handle);
  }, [scoped, value, open]);

  const filteredBrandOptions = useMemo(() => {
    const term = value.trim().toLowerCase();
    return term ? brandOptions.filter(b => b.brand_name.toLowerCase().includes(term)) : brandOptions;
  }, [brandOptions, value]);

  const showDropdown = open && (scoped ? filteredBrandOptions.length > 0 : results.length > 0);
  const rect = useAnchoredRect(anchorRef, showDropdown);
  // Whichever list is actually showing right now — scoped brand suggestions
  // or the pre-drug-chosen brand-inclusive fallback — unified into one list
  // so a single keyboard-nav hook instance can drive both without violating
  // the rules of hooks by calling it conditionally.
  const activeItems: (MedicineBrandResult | MedicineSearchResult)[] = scoped ? filteredBrandOptions : results;
  const { activeIndex, onKeyDown } = useDropdownKeyNav(activeItems, showDropdown, item => {
    if (scoped) {
      const b = item as MedicineBrandResult;
      onPick({
        id: b.id, generic_name: genericName, brand_name: b.brand_name, strength: b.strength,
        dosage_form: b.dosage_form, drug_id: b.drug_id, manufacturer: b.manufacturer,
      });
    } else {
      onPick(item as MedicineSearchResult);
    }
    setOpen(false);
  }, () => setOpen(false));

  return (
    <div className="relative" ref={anchorRef}>
      <Input
        value={value}
        onChange={e => { onChange(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        onKeyDown={onKeyDown}
        placeholder="Search brand…"
        className="w-32"
      />
      {rect && showDropdown && createPortal(
        <div
          className="fixed z-50 max-h-48 overflow-y-auto rounded-[var(--radius)] border border-border bg-surface shadow-md"
          style={{ top: rect.top, left: rect.left, width: Math.max(rect.width, 224) }}
        >
          {scoped
            ? filteredBrandOptions.map((b, idx) => (
                <button
                  key={b.id}
                  type="button"
                  className={`block w-full px-2 py-1.5 text-left text-[12px] hover:bg-muted/60 ${idx === activeIndex ? 'bg-muted/60' : ''}`}
                  onMouseDown={e => {
                    e.preventDefault();
                    onPick({
                      id: b.id, generic_name: genericName, brand_name: b.brand_name, strength: b.strength,
                      dosage_form: b.dosage_form, drug_id: b.drug_id, manufacturer: b.manufacturer,
                    });
                    setOpen(false);
                  }}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-medium">{b.formulary_status === 'preferred' ? '⭐ ' : ''}{b.brand_name}</span>
                    {b.in_stock
                      ? <span className="whitespace-nowrap text-[10px] font-semibold text-success">✓ In stock</span>
                      : <span className="whitespace-nowrap text-[10px] text-ink-muted">Not stocked here</span>}
                  </div>
                  {b.strength && <div className="text-ink-muted">{b.strength}</div>}
                </button>
              ))
            : results.map((m, idx) => (
                <button
                  key={m.id}
                  type="button"
                  className={`block w-full px-2 py-1.5 text-left text-[12px] hover:bg-muted/60 ${idx === activeIndex ? 'bg-muted/60' : ''}`}
                  onMouseDown={e => { e.preventDefault(); onPick(m); setOpen(false); }}
                >
                  <div className="font-medium">{m.brand_name || m.generic_name}</div>
                  <div className="text-ink-muted">{m.generic_name}{m.strength ? ` · ${m.strength}` : ''}</div>
                </button>
              ))}
        </div>,
        document.body
      )}
    </div>
  );
}

export function PrescriptionBuilder({ patientId, visitId, uhid, visitCode, aiClinicalContext, patientName, patientAge, patientGender, vitals }: PrescriptionBuilderProps) {
  // AI off (§4.4): "Add by voice", "Draft with AI" and "Fill with AI" are all
  // hidden; "Generate prescription sheet" stays — it's template-only and
  // needs no AI call (companion doc C12).
  const { aiEnabled } = useAiAvailability();
  const [prescriptionId, setPrescriptionId] = useState<number | null>(null);
  const [status, setStatus] = useState<PrescriptionStatus>('draft');
  const [items, setItems] = useState<PrescriptionItemInput[]>([]);
  const [dirty, setDirty] = useState(false);
  const [actionError, setActionError] = useState('');
  const [safetyAlerts, setSafetyAlerts] = useState<SafetyAlert[]>([]);
  const [resumed, setResumed] = useState(false);
  const [voiceAdding, setVoiceAdding] = useState(false);
  const [aiPanelOpen, setAiPanelOpen] = useState(false);
  const [aiInstructions, setAiInstructions] = useState('');
  const [aiDrafting, setAiDrafting] = useState(false);
  const [aiError, setAiError] = useState('');
  // Phase 4 (Doctor AI Memory, §10) — extends Phase 2/3's "personalised with
  // N of your preferences" chip and the learned-preference proposal card,
  // already on ConsultationChat/DischargeSummaryChat, to OPD Prescription.
  // Two separate applied-memory slots because drafting (a fresh AI proposal)
  // and sheet-filling (polishing an existing sheet's open fields) are
  // different calls that can each apply a different subset of preferences.
  const [appliedDraftMemory, setAppliedDraftMemory] = useState<AppliedMemoryItem[]>([]);
  const [appliedSheetMemory, setAppliedSheetMemory] = useState<AppliedMemoryItem[]>([]);
  const [showPreferences, setShowPreferences] = useState(false);
  const [proposal, setProposal] = useState<DoctorPreference | null>(null);
  const proposalCheckedRef = useRef(false);

  // "Generate prescription sheet" — the full letterheaded, downloadable
  // Prescription document, saved through the same POST /api/consultation/save
  // + docx pipeline Consultation Notes and Discharge Summary use. Built from
  // the SAVED prescription rows without AI (GET /api/prescriptions/:id/sheet —
  // docs/HMS_V4_AI_CONTEXT_BUILDING_ASSESSMENT.md §5.2 C12, decision D18), so
  // it works with AI switched off; "✨ Fill with AI" is an optional extra.
  // Edited in place in DocumentEditor. Before approval its Rx. lines are
  // compared with the saved prescription, and a difference must be confirmed.
  const [sheetOpen, setSheetOpen] = useState(false);
  const [sheetLoading, setSheetLoading] = useState(false);
  const [sheetAiFilling, setSheetAiFilling] = useState(false);
  const [sheetError, setSheetError] = useState('');
  const [sheetDoc, setSheetDoc] = useState<string | null>(null);
  const [sheetEditDoc, setSheetEditDoc] = useState('');
  // C6 — see ConsultationChat.tsx's own editDocRef comment: the ↻ button is
  // a keyed decoration widget ProseMirror can reuse (stale event listener)
  // across rebuilds, so regenerateSheetSection reads the sheet through this
  // ref rather than closing over sheetEditDoc directly.
  const sheetEditDocRef = useRef(sheetEditDoc);
  useEffect(() => { sheetEditDocRef.current = sheetEditDoc; }, [sheetEditDoc]);
  // C6 — see ConsultationChat.tsx's own regeneratingKey comment.
  const [sheetRegeneratingKey, setSheetRegeneratingKey] = useState<string | null>(null);
  const [sheetSource, setSheetSource] = useState<'template' | 'ai'>('template');
  // content: the sheet text that was checked, so only a real edit dismisses the warning.
  const [sheetMismatch, setSheetMismatch] = useState<(SheetComparison & { content: string }) | null>(null);
  const [sheetApproved, setSheetApproved] = useState(false);
  const [approvedSheet, setApprovedSheet] = useState<{ id: number; uhid: string } | null>(null);
  const [sheetSaveError, setSheetSaveError] = useState('');

  // Closing out a finished course ("Mark completed" / "Cancel") is not offered
  // here: this screen only shows the prescription being written in this visit,
  // whose course hasn't run yet. It lives in Patient Records → Prescriptions
  // (features/prescriptions/PatientPrescriptionHistory.tsx), which lists every
  // visit's prescriptions.

  const existingQuery = useVisitPrescriptions(visitId);
  const diagnosesQuery = useVisitDiagnoses(visitCode || '');
  const createMutation = useCreatePrescription(uhid);
  const saveMutation = useSavePrescription(uhid);
  const statusMutation = useSetPrescriptionStatus(uhid);

  // Resume the visit's existing draft/active prescription on mount (there
  // can be an earlier one from a page refresh mid-consultation) rather than
  // starting a second one — see usePrescriptionDraft.ts. existingQuery's rows
  // come from the LIST endpoint, which (per HMS_V4_API_CONTRACT.md) returns
  // only an item_count summary, not the items themselves — so resuming needs
  // a second, detail-endpoint fetch to actually get the medicine rows.
  useEffect(() => {
    if (resumed || !existingQuery.data) return;
    const current = existingQuery.data.find(p => p.status === 'draft' || p.status === 'active');
    if (current) {
      setPrescriptionId(current.id);
      setStatus(current.status);
      getPrescriptionVersioned(current.id).then(({ data }) => {
        setItems(data.items.length ? data.items.map(({ id: _id, prescription_id: _pid, created_at: _ca, source: _src, ...rest }) => rest) : []);
      }).catch(() => {});
    }
    setResumed(true);
  }, [existingQuery.data, resumed]);

  // "Repeat last prescription" (UX opportunity from the redesign review): a
  // chronic patient often gets the same medicines refilled visit after
  // visit — offer to pre-fill from the most recent *other* visit's
  // prescription rather than the doctor re-typing it. patientRxQuery's rows
  // come from the LIST endpoint too (item_count only), same as existingQuery
  // above, so applying one still needs its own detail fetch.
  const patientRxQuery = usePatientPrescriptions(uhid ?? null);
  const [repeatDismissed, setRepeatDismissed] = useState(false);
  const [repeatBusy, setRepeatBusy] = useState(false);
  const lastPrescription = (patientRxQuery.data ?? []).find(
    p => p.visit_id !== visitId && (p.status === 'active' || p.status === 'completed')
  );
  const applyRepeatLast = async () => {
    if (!lastPrescription) return;
    setRepeatBusy(true);
    try {
      const { data } = await getPrescriptionVersioned(lastPrescription.id);
      setItems(data.items.map(({ id: _id, prescription_id: _pid, created_at: _ca, source: _src, ...rest }, i) => ({ ...rest, sequence: i + 1 })));
      setDirty(true);
    } finally {
      setRepeatBusy(false);
    }
  };

  const addItem = () => { setItems(p => [...p, emptyItem(p.length + 1)]); setDirty(true); };

  // v3 Consultation layout's "→ Move these to the prescription" hand-off
  // (planHandoff.ts) — this component is already mounted (forceMount)
  // beside the note canvas, so a plain event reaches it without a prop
  // threaded through ConsultStage.tsx just to carry this one payload.
  // Reuses the same table parser the "Draft with AI"/"Fill with AI" flows
  // already fall back to (parseAiPrescriptionTable, above) rather than a
  // second implementation of the same table shape.
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<PlanHandoffDetail>).detail;
      if (!detail?.text) return;
      const parsed = parseAiPrescriptionTable(detail.text, items.length + 1);
      if (!parsed.length) {
        toast({ variant: 'danger', title: 'Could not read the Plan table', description: "The rows didn't match a Medicine/Dose/Frequency table shape." });
        return;
      }
      setItems(p => [...p, ...parsed]);
      setDirty(true);
      toast({ variant: 'success', title: `${parsed.length} medicine${parsed.length === 1 ? '' : 's'} added from the note's Plan` });
    };
    window.addEventListener(PLAN_HANDOFF_EVENT, handler);
    return () => window.removeEventListener(PLAN_HANDOFF_EVENT, handler);
  }, [items.length]);
  const applyVoiceItem = (extracted: Record<string, string>) => {
    setItems(p => [...p, itemFromVoiceFields(extracted, p.length + 1)]);
    setVoiceAdding(false);
    setDirty(true);
  };
  const removeItem = (idx: number) => {
    setItems(p => p.filter((_, i) => i !== idx).map((it, i) => ({ ...it, sequence: i + 1 })));
    setDirty(true);
  };
  const updateItem = (idx: number, patch: Partial<PrescriptionItemInput>) => {
    setItems(p => p.map((it, i) => {
      if (i !== idx) return it;
      const merged = { ...it, ...patch };
      // Auto-fill a still-empty Quantity as dose/frequency/duration are
      // typed in — never overwrites a value the doctor set directly, either
      // already on the row or in this very patch (the Quantity cell's own
      // onChange always includes `quantity` in its patch).
      const touchedDoseFields = (['dose_quantity', 'frequency_code', 'duration_value', 'duration_unit'] as const).some(k => k in patch);
      if (touchedDoseFields && merged.quantity == null && !('quantity' in patch)) {
        merged.quantity = deriveQuantity(merged);
      }
      return merged;
    }));
    setDirty(true);
  };

  // C9 (§5.2) — "Draft with AI" is a thread: the first turn proposes fresh
  // lines from clinical context (unchanged from before); every later turn
  // sends the AI its own prior output plus the doctor's instruction
  // ("Change Amlodipine to 10mg") and reconciles the fresh reply against the
  // table by drug name — see prescriptionAiReconcile.ts's own header for why
  // that's full-list-in/full-list-out rather than a diff/patch format.
  // `threadActive` needs no separate state: an AI-owned row (ai_proposal_id
  // set) already marks that a thread exists, and it's set synchronously
  // before the next turn can start.
  const threadActive = items.some(it => it.ai_proposal_id != null);

  // Drafts medicine lines from clinical context (this visit's diagnoses +
  // vitals/chief-complaints + whatever the doctor typed) and appends them as
  // editable rows — nothing is saved or activated by this step. Mirrors the
  // "AI proposes, doctor reviews and approves" pattern used everywhere else
  // AI touches clinical data in this app (see utils/ipSafetyRules.js's gist,
  // ConsultationChat's own approve step).
  const draftWithAI = async () => {
    setAiError('');
    setAiDrafting(true);
    setAppliedDraftMemory([]);
    try {
      let instruction: string;
      if (!threadActive) {
        const diagnosisLine = (diagnosesQuery.data ?? [])
          .map(d => `${d.diagnosis}${d.rank === 1 ? ' (primary)' : ''}`)
          .join(', ');
        const contextLines = [
          aiClinicalContext,
          diagnosisLine ? `Diagnoses recorded for this visit: ${diagnosisLine}` : null,
          aiInstructions.trim() ? `Doctor's instructions: ${aiInstructions.trim()}` : null,
        ].filter(Boolean).join('\n');

        instruction = `Propose a DRAFT prescription for this patient based on the clinical context below. This is a draft only — the prescribing doctor will review, correct and approve every line before it is saved or dispensed, so always produce your best-judgement draft rather than asking a clarifying question, even if some details (e.g. weight, allergies, exact vitals) are not provided; use standard adult dosing conventions for anything unspecified. Output ONLY the medicine lines — no heading, no patient details, no advice, no follow-up, no commentary, no questions. Each line must be in exactly this format:
Rx. <drug name> <strength> — <dose quantity> <dose unit>, <route>, <frequency: OD|BD|TDS|QID|HS|SOS|STAT> × <duration value><unit: d|wk|mo>
Example: Rx. Paracetamol 650mg — 1 tab, Oral, TDS × 5d
Use generic drug names.

Clinical context:
${contextLines || 'None recorded — use clinical judgement based on the diagnoses above.'}`;
      } else {
        instruction = `${aiInstructions.trim()}

Current draft medicine list — revise it as asked and return the complete updated list, one line per medicine, in exactly this format:
Rx. <drug name> <strength> — <dose quantity> <dose unit>, <route>, <frequency: OD|BD|TDS|QID|HS|SOS|STAT> × <duration value><unit: d|wk|mo>
Keep every medicine that isn't affected by this instruction exactly as it was. Use generic drug names.

Current medicine list:
${formatItemsAsRxLines(items)}`;
      }

      const cfg = loadConfig();
      const messages: AIMessage[] = [{ role: 'user', content: instruction }];
      const r = await authFetch('/api/ai/generate-document', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          provider: cfg.provider, model: getActiveModel(cfg), messages,
          doc_type: 'OPD Prescription',
          // An empty patient object here reads to the model as "every
          // demographic is unknown" — combined with a profile that says
          // never assume missing information, that alone is enough for a
          // careful model to refuse to draft at all rather than propose
          // lines, independent of the Rx./table format question.
          patient: { name: patientName, age: patientAge, gender: patientGender, uhid },
          vitals: vitals || {}, extra_context: aiClinicalContext,
          temperature: cfg.temperature, max_tokens: cfg.maxTokens,
          // Phase 4: personalize drafting with this doctor's own preferences
          // (e.g. wording/formatting habits) — a no-op, byte-identical call
          // for a doctor with none active. No 'suggestions' capability here:
          // the reply is parsed strictly as Rx. lines, never shown as prose
          // for a "remember" chip to land against, turn 1 or turn N alike.
          capabilities: ['memory'],
        }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Backend error');
      const reply: string = d.content;
      setAppliedDraftMemory(d.applied_memory ?? []);

      const drafted = parseAiPrescriptionLines(reply, items.length + 1);
      const rows = drafted.length ? drafted : parseAiPrescriptionTable(reply, items.length + 1);
      // An empty result is only ever an error on the first turn (nothing to
      // parse means the draft failed). On a later turn it can legitimately
      // mean "remove the only/last AI-owned medicine" (e.g. the model replies
      // "[No medicines prescribed]") — that must still reach reconciliation,
      // which correctly drops every AI-owned row nothing in `rows` matches.
      if (!rows.length && !threadActive) {
        setAiError("Could not find any prescription lines in the AI's reply — try adding more detail to the instructions.");
        return;
      }

      if (!threadActive) {
        // Record what the AI proposed, so later saves can tell which lines
        // the doctor kept, edited or deleted. Instrumentation only: if
        // recording fails the rows are still added, just without proposal ids.
        let tagged = rows;
        try {
          const { ids } = await recordPrescriptionAiProposals({ patient_id: patientId, visit_id: visitId, lines: rows });
          tagged = rows.map((row, i) => ({ ...row, ai_proposal_id: ids[i] ?? null }));
        } catch { /* non-fatal — see comment above */ }
        setItems(p => [...p, ...tagged]);
      } else {
        const { items: reconciled, toRecordIndices } = reconcileAiPrescriptionLines(items, rows);
        if (toRecordIndices.length) {
          try {
            const { ids } = await recordPrescriptionAiProposals({
              patient_id: patientId, visit_id: visitId,
              lines: toRecordIndices.map(i => reconciled[i]!),
            });
            toRecordIndices.forEach((itemIdx, k) => {
              reconciled[itemIdx] = { ...reconciled[itemIdx]!, ai_proposal_id: ids[k] ?? null };
            });
          } catch { /* non-fatal — see comment above */ }
        }
        setItems(reconciled);
      }
      setDirty(true);
      // The panel stays open once a thread is active, so the doctor can keep
      // refining — only the very first draft auto-closes it.
      if (!threadActive) setAiPanelOpen(false);
      setAiInstructions('');
    } catch (e) {
      setAiError(e instanceof Error ? e.message : String(e));
    } finally {
      setAiDrafting(false);
    }
  };

  const validItems = items.filter(it => it.drug_name.trim());

  // Live, deterministic allergy/duplicate-therapy check WHILE TYPING, before
  // any save — the highest-value moment to catch it, since a doctor who
  // sees this before clicking Save or Activate can just not add the drug.
  // Debounced the same way BrandNameCell's own medicine search is, and
  // re-run on every edit to validItems (add/remove/rename a line) or a
  // status change (activating re-checks against a a fresh set of "other
  // active prescriptions" too). See utils/rxSafetyChecks.js — ADVISORY
  // ONLY, never wired into any disabled prop below.
  useEffect(() => {
    if (!validItems.length) { setSafetyAlerts([]); return; }
    const handle = setTimeout(() => {
      checkPrescriptionSafety(patientId, validItems, prescriptionId ?? undefined)
        .then(r => setSafetyAlerts(r.alerts))
        .catch(() => { /* advisory only — a failed check is silent, not a blocker */ });
    }, 500);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(validItems.map(it => it.drug_name)), patientId, prescriptionId]);

  // Builds the sheet from the saved prescription — no AI. The sheet must match
  // what Pharmacy will dispense, so unsaved table edits are saved first
  // (activate() already has, and passes skipDirtyCheck).
  const generateSheet = async (id: number | null = prescriptionId, { skipDirtyCheck = false } = {}) => {
    setSheetError('');
    setSheetMismatch(null);
    if (id == null) {
      setSheetError('Save the prescription first — the sheet is built from the saved medicines.');
      return;
    }
    if (dirty && !skipDirtyCheck) {
      setSheetError('Save your changes to the medicines first — the sheet is built from the saved prescription.');
      return;
    }
    setSheetLoading(true);
    try {
      const sheet = await getPrescriptionSheet(id);
      setSheetDoc(sheet.content);
      setSheetEditDoc(sheet.content);
      setSheetSource('template');
      setSheetApproved(false);
      setAppliedSheetMemory([]);
      setSheetOpen(true);
    } catch (e) {
      setSheetError(e instanceof Error ? e.message : String(e));
    } finally {
      setSheetLoading(false);
    }
  };

  // Optional: asks the AI to fill the sheet's open fields (Diagnosis, Advice,
  // Follow-up) from the visit's clinical data (aiClinicalContext: triage,
  // allergies, this visit's diagnoses, history) and tidy the advice. Every Rx.
  // line must stay exactly as written — and if one changes anyway, the
  // approval-time comparison below catches it.
  const fillSheetWithAi = async () => {
    if (!sheetEditDoc.trim()) return;
    setSheetError('');
    setSheetAiFilling(true);
    setAppliedSheetMemory([]);
    try {
      const cfg = loadConfig();
      const instruction = `Complete this prescription sheet using the clinical data provided for this visit. Fill each field still to fill (such as Diagnosis, Advice and Follow-up) only where that data supports a value, and leave any field it doesn't support exactly as it is for the doctor. Make the patient advice clear and practical. Every line that starts with "Rx." must stay exactly as written, character for character, and so must the Date, Doctor and Allergies lines and any value already written. Do not add, remove or change any medicine, dose, frequency or duration. Return the complete sheet.\n\n${sheetEditDoc}`;
      const result = await generateDocument({
        model: getActiveModel(cfg),
        messages: [{ role: 'user', content: instruction }],
        doc_type: 'OPD Prescription',
        // A real patient object, never {} — see CLAUDE.md's "Always send real
        // patient/vitals objects" note.
        patient: { name: patientName, age: patientAge, gender: patientGender, uhid },
        vitals: (vitals || {}) as Record<string, unknown>,
        extra_context: aiClinicalContext,
        temperature: cfg.temperature,
        max_tokens: cfg.maxTokens,
        // Phase 4: same personalization as drafting above, plus the sheet
        // stays in scope for whatever memory_doc_types already permits.
        capabilities: ['smart_fields', 'memory'],
      });
      const polished = cleanAIDraft(result.content);
      setAppliedSheetMemory(result.applied_memory ?? []);
      setSheetDoc(polished);
      setSheetEditDoc(polished);
      setSheetSource('ai');
      setSheetMismatch(null);
    } catch (e) {
      setSheetError(e instanceof Error ? e.message : String(e));
    } finally {
      setSheetAiFilling(false);
    }
  };

  // C6 (§5.2) — see ConsultationChat.tsx's own regenerateSection. Rx. lines
  // never match a heading shape, so the ↻ button only ever appears next to
  // Diagnosis/Advice/Follow-up-style lines — the same instruction's "every
  // Rx. line must stay exactly as written" sentence is kept anyway, since
  // the sheet-approval mismatch check is the safety net either way.
  const regenerateSheetSection = async ({ key, heading }: HeadingSection) => {
    if (sheetRegeneratingKey) return;
    setSheetRegeneratingKey(key);
    try {
      const cfg = loadConfig();
      const current = sheetEditDocRef.current;
      const instruction = `Regenerate only the "${heading}" section below. Return the complete prescription sheet with every other line — including every "Rx." line, and the Date, Doctor and Allergies lines — kept byte-for-byte identical. Do not add, remove or change any medicine, dose, frequency or duration, and do not rename, remove, reorder or add any section.\n\nCurrent prescription sheet:\n\n${current}`;
      const result = await generateDocument({
        model: getActiveModel(cfg),
        messages: [{ role: 'user', content: instruction }],
        doc_type: 'OPD Prescription',
        patient: { name: patientName, age: patientAge, gender: patientGender, uhid },
        vitals: (vitals || {}) as Record<string, unknown>,
        extra_context: aiClinicalContext,
        temperature: cfg.temperature,
        max_tokens: cfg.maxTokens,
        capabilities: ['smart_fields', 'memory'],
      });
      const cleaned = cleanAIDraft(result.content);
      const spliced = spliceSectionFromReply(current, cleaned, key);
      if (spliced == null) {
        toast({ variant: 'danger', title: 'Could not regenerate that section', description: "The AI's reply didn't include a matching section — nothing changed." });
        return;
      }
      setSheetDoc(spliced); setSheetEditDoc(spliced);
      setSheetMismatch(current => (current && current.content !== spliced ? null : current));
      toast({ variant: 'success', title: `Regenerated: ${heading}` });
    } catch (e) {
      toast({ variant: 'danger', title: 'Could not regenerate that section', description: e instanceof Error ? e.message : undefined });
    } finally {
      setSheetRegeneratingKey(null);
    }
  };

  const sheetUnfilled = useMemo(() => unfilledFieldLabels(sheetEditDoc), [sheetEditDoc]);

  const sheetInsertables = useMemo<InsertableValue[]>(() => {
    const items: InsertableValue[] = [
      { key: 'today', label: "Today's date", value: formatDocDate(new Date()) },
      { key: 'follow-up', label: 'Follow-up date (to fill in)', value: '{{date|Follow-up date}}' },
    ];
    const doctorName = getAuthUser()?.full_name;
    if (doctorName) items.push({ key: 'doctor', label: 'Doctor name', value: doctorName });
    return items;
  }, []);

  const approveSheet = async (acknowledgeMismatch = false) => {
    const content = sheetEditDoc;
    if (!content.trim()) return;
    if (sheetUnfilled.length) {
      setSheetSaveError(`Fill in or remove ${sheetUnfilled.length === 1 ? 'this field' : 'these fields'} first: ${sheetUnfilled.join(', ')}`);
      return;
    }
    setSheetSaveError('');
    try {
      // Early warning: show what differs from the saved prescription before
      // saving. POST /api/consultation/save runs the same comparison
      // authoritatively and refuses an unacknowledged difference.
      if (!acknowledgeMismatch && prescriptionId != null) {
        const comparison = await checkPrescriptionSheet(prescriptionId, content);
        if (comparison.applies && !comparison.matches) {
          setSheetMismatch({ ...comparison, content });
          return;
        }
      }
      const saved = await saveConsultation({
        patient_id: patientId,
        queue_id: null,
        // Without this the prescription document is orphaned: nothing ties it
        // to the visit it was written in, so Pharmacy's pending list (which is
        // driven by these consultation rows) can never tell that this visit's
        // Pharmacy stage already concluded, and the row stays "pending" all
        // day. routes/consultation.js uses it for provenance only — the
        // stage-advance block there is gated on queue_id, which stays null, so
        // this cannot advance the visit past Consultation.
        visit_id: visitId,
        doc_type: 'OPD Prescription',
        ai_draft: sheetDoc,
        final_doc: content,
        generation_source: sheetSource,
        rx_mismatch_acknowledged: acknowledgeMismatch || undefined,
        approved: true,
        approved_by: getAuthUser()?.full_name || 'doctor',
      });
      setSheetApproved(true);
      setSheetMismatch(null);
      if (saved.document_id) setApprovedSheet({ id: saved.document_id, uhid: saved.document_uhid });

      // Phase 3/4: at most one learned-preference proposal per session,
      // checked once right after the first successful sheet approval —
      // mirrors ConsultationChat.tsx's own approve().
      if (!proposalCheckedRef.current) {
        proposalCheckedRef.current = true;
        getNextProposal('OPD Prescription').then((r) => setProposal(r.proposal)).catch(() => {});
      }
    } catch (e) {
      setSheetSaveError(e instanceof Error ? e.message : String(e));
    }
  };

  const save = async () => {
    setActionError('');
    try {
      if (prescriptionId == null) {
        const created = await createMutation.mutateAsync({
          patient_id: patientId, visit_id: visitId, status: 'draft', items: validItems,
        });
        setPrescriptionId(created.id);
        setStatus(created.status);
        if (created.alerts) setSafetyAlerts(created.alerts);
      } else {
        const saved = await saveMutation.mutateAsync({ id: prescriptionId, patch: { items: validItems } });
        setStatus(saved.status);
        if (saved.alerts) setSafetyAlerts(saved.alerts);
      }
      setDirty(false);
    } catch (e) {
      setActionError(e instanceof Error ? e.message : String(e));
    }
  };

  const activate = async () => {
    if (!validItems.length) { setActionError('A prescription cannot be activated with no medicines on it.'); return; }
    // Mirrors the backend's own requireDose rule (routes/prescriptions.js's
    // insertItems/assertActivationDoseRequirement) — checked client-side too
    // so the doctor sees this immediately rather than after a round trip.
    // SOS/STAT is the only genuinely open-ended case; anything else with no
    // dose recorded is a gap, not a deliberate PRN order — see
    // DispensingTab.tsx's matching "No quantity limit recorded" badge, which
    // this prevents from ever needing to fire for a NEW prescription.
    const missingDose = validItems.filter(
      it => it.quantity == null && !['SOS', 'STAT'].includes((it.frequency_code || '').trim().toUpperCase())
    );
    if (missingDose.length) {
      setActionError(
        `Enter a dose (or set frequency to SOS/STAT for a genuinely as-needed order) before activating — missing on: ${missingDose.map(it => it.drug_name || 'this line').join(', ')}.`
      );
      return;
    }
    setActionError('');
    // Captured before the mutations below reset it — determines whether the
    // sheet needs regenerating (see the comment on that call further down).
    const wasDirty = dirty;
    try {
      let id = prescriptionId;
      if (id == null) {
        const created = await createMutation.mutateAsync({
          patient_id: patientId, visit_id: visitId, status: 'active', items: validItems,
        });
        id = created.id;
        setPrescriptionId(id);
        setStatus(created.status);
        if (created.alerts) setSafetyAlerts(created.alerts);
      } else {
        if (dirty) await saveMutation.mutateAsync({ id, patch: { items: validItems } });
        const activated = await statusMutation.mutateAsync({ id, status: 'active' });
        setStatus(activated.status);
        if (activated.alerts) setSafetyAlerts(activated.alerts);
      }
      setDirty(false);
      // A populated, activated prescription should always have a sheet to
      // print/hand to the patient — generate it now rather than leaving that
      // as a manual step the doctor might skip, and rebuild it whenever the
      // medicine table changed since the sheet was last built (its Rx. lines
      // are built from the saved rows, so a stale sheet would print medicines
      // the doctor no longer prescribed, or omit ones added since). Also
      // rebuildable manually via "Generate prescription sheet". No AI.
      if (!sheetDoc || wasDirty) generateSheet(id, { skipDirtyCheck: true });
    } catch (e) {
      setActionError(e instanceof Error ? e.message : String(e));
    }
  };

  // Includes sheetLoading so Save/Activate stay visibly busy through the
  // whole flow — activate() kicks off generateSheet() as a background step
  // (see its own comment above), and without this the Activate button's own
  // spinner stopped the instant the status mutation resolved while sheet
  // generation kept running silently in a small ghost button the doctor's
  // attention had already moved away from.
  const busy = createMutation.isPending || saveMutation.isPending || statusMutation.isPending || sheetLoading;
  // Matches the backend's actual policy (PUT /api/prescriptions/:id only
  // blocks completed/cancelled/entered-in-error) — an activated prescription
  // is still the doctor's live document until one of those terminal states,
  // not locked the moment it's populated.
  const editable = status === 'draft' || status === 'active';
  // Once a prescription is active and untouched since, clicking Activate
  // again is a no-op that looks broken (status doesn't change, the sheet
  // isn't regenerated) — disable it until an edit actually makes it dirty
  // again. Doesn't apply to a fresh draft (dirty starts false there too,
  // but "create as active" is always the right action for a populated,
  // never-yet-activated prescription).
  const nothingToActivate = status === 'active' && !dirty;

  return (
    <Card>
      <CardHeader>
        <CardTitle>
          💊 Prescription
          <Badge variant={status === 'active' ? 'success' : status === 'draft' ? 'neutral' : 'info'}>{status}</Badge>
        </CardTitle>
        {editable && (
          <div className="flex gap-2">
            <Button size="sm" variant="ghost" onClick={addItem}>+ Add medicine</Button>
            {aiEnabled && (
              <Button size="sm" variant="ghost" onClick={() => setVoiceAdding(v => !v)}>
                {voiceAdding ? '✕ Cancel' : '🎙 Add by voice'}
              </Button>
            )}
            {aiEnabled && (
              <Button size="sm" variant="ghost" onClick={() => setAiPanelOpen(v => !v)}>
                {aiPanelOpen ? '✕ Cancel' : '🤖 Draft with AI'}
              </Button>
            )}
            <Button size="sm" variant="ghost" loading={sheetLoading} onClick={() => generateSheet()}>
              📄 Generate prescription sheet
            </Button>
          </div>
        )}
      </CardHeader>
      <CardContent>
        {existingQuery.isLoading && <LoadingState label="Checking for an existing prescription…" />}
        {existingQuery.isError && (
          <ErrorState
            message={existingQuery.error instanceof ApiError ? existingQuery.error.message : 'Could not load prescriptions for this visit'}
            onRetry={() => existingQuery.refetch()}
          />
        )}

        {voiceAdding && editable && (
          <div className="mb-2.5">
            <VoiceFormFiller fields={ITEM_VOICE_FIELDS} onFields={applyVoiceItem} label="🎙 Describe this medicine" />
          </div>
        )}

        {aiPanelOpen && editable && (
          <div className="mb-2.5 flex flex-col gap-2 rounded-[var(--radius)] border border-border p-3">
            <div className="text-[12px] text-ink-muted">
              {threadActive
                ? 'Keep refining — e.g. "Change Amlodipine to 10mg" or "Remove the Paracetamol" — then review every line before saving.'
                : "Drafts medicine lines from this visit's diagnoses and vitals — add anything else the AI should account for below, then review every line before saving."}
            </div>
            <div className="flex items-center gap-1">
              <Input
                value={aiInstructions}
                onChange={e => setAiInstructions(e.target.value)}
                placeholder={threadActive ? 'e.g. Change Amlodipine to 10mg' : 'Optional — e.g. patient has a penicillin allergy, keep it to 3 days'}
                className="flex-1"
              />
              <VoiceMicButton onText={t => setAiInstructions(v => (v ? v + ' ' : '') + t)} title="Dictate instructions" />
            </div>
            {aiError && <div className="text-[12px] text-danger">{aiError}</div>}
            <div>
              <Button size="sm" loading={aiDrafting} onClick={draftWithAI}>{threadActive ? 'Send' : 'Generate'}</Button>
            </div>
          </div>
        )}

        {/* threadActive keeps the panel open across turns (a doctor may keep
            refining), so this chip is rendered whenever there's applied
            memory to show, whether or not the panel happens to be open. */}
        {appliedDraftMemory.length > 0 && (
          <div className="mb-2.5">
            <PersonalisedChip applied={appliedDraftMemory} onOpenPreferences={() => setShowPreferences(true)} />
          </div>
        )}

        {!editable && (
          <div className="mb-2 text-[12px] text-ink-muted">
            This prescription is {status} and can no longer be edited here. {status !== 'entered-in-error' && 'Write a new one instead.'}
          </div>
        )}

        {items.length === 0 && editable && resumed && lastPrescription && !repeatDismissed && (
          <div className="mb-2.5 flex flex-wrap items-center gap-2 rounded-[var(--radius)] border border-border p-2.5 text-[12.5px]">
            <span>🔁 Repeat the prescription from {lastPrescription.visit_code || 'the last visit'}?</span>
            <Button size="sm" loading={repeatBusy} onClick={applyRepeatLast}>Add those medicines</Button>
            <Button size="sm" variant="ghost" onClick={() => setRepeatDismissed(true)}>Not this time</Button>
          </div>
        )}

        {items.length === 0 && editable && (
          <div className="text-[13px] text-ink-muted">No medicines added yet.</div>
        )}

        <SafetyAlertBanner alerts={safetyAlerts} />

        {items.length > 0 && (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Drug</TableHead>
                <TableHead>Brand</TableHead>
                <TableHead>Strength</TableHead>
                <TableHead>Dose</TableHead>
                <TableHead>Route</TableHead>
                <TableHead>Frequency</TableHead>
                <TableHead>Duration</TableHead>
                <TableHead>Quantity</TableHead>
                <TableHead>Substitution</TableHead>
                {editable && <TableHead />}
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((it, idx) => (
                <TableRow key={idx}>
                  <TableCell>
                    {editable ? (
                      <DrugNameCell
                        value={it.drug_name}
                        // Typing over an already-resolved generic (rather than
                        // picking a new suggestion) invalidates drug_id the
                        // same way editing the brand invalidates product_id —
                        // the text no longer necessarily matches what was
                        // resolved, so the structured links and the formulary
                        // badge above must not keep showing a stale match.
                        onChange={t => updateItem(idx, { drug_name: t, drug_id: undefined, product_id: undefined, manufacturer_snapshot: undefined })}
                        // Picking a new generic invalidates whatever brand was
                        // previously on this line (§16 of the Drug Master
                        // architecture doc) — medicine_id/product_id/
                        // manufacturer_snapshot all clear, not just brand_name.
                        onPick={med => updateItem(idx, {
                          drug_name: med.generic_name, brand_name: '',
                          medicine_id: undefined, drug_id: med.drug_id ?? undefined,
                          product_id: undefined, manufacturer_snapshot: undefined,
                        })}
                      />
                    ) : it.drug_name}
                    <FormularyBadge drugId={it.drug_id} />
                  </TableCell>
                  <TableCell>
                    {editable ? (
                      <BrandNameCell
                        value={it.brand_name ?? ''}
                        genericName={it.drug_name}
                        drugId={it.drug_id}
                        onChange={t => updateItem(idx, {
                          // Clearing/editing a resolved brand explicitly
                          // un-resolves the structured link — §16's "clears a
                          // previously-selected product" row. brand_name stays
                          // whatever the doctor typed, free text either way.
                          brand_name: t, product_id: undefined, manufacturer_snapshot: undefined,
                        })}
                        onPick={med => updateItem(idx, {
                          brand_name: med.brand_name || med.generic_name,
                          drug_name: it.drug_name.trim() ? it.drug_name : med.generic_name,
                          strength: it.strength ? it.strength : (med.strength ?? undefined),
                          medicine_id: med.id,
                          product_id: med.id,
                          drug_id: med.drug_id ?? it.drug_id ?? undefined,
                          manufacturer_snapshot: med.manufacturer ?? undefined,
                        })}
                      />
                    ) : (it.brand_name || '—')}
                  </TableCell>
                  <TableCell>
                    {editable ? (
                      <Input value={it.strength ?? ''} onChange={e => updateItem(idx, { strength: e.target.value })} placeholder="500mg" className="w-24" />
                    ) : it.strength}
                  </TableCell>
                  <TableCell>
                    {editable ? (
                      <div className="flex gap-1">
                        <Input type="number" value={it.dose_quantity ?? ''} onChange={e => updateItem(idx, { dose_quantity: e.target.value === '' ? null : Number(e.target.value) })} className="w-16" />
                        <Input value={it.dose_unit ?? ''} onChange={e => updateItem(idx, { dose_unit: e.target.value })} placeholder="tab" className="w-16" />
                      </div>
                    ) : `${it.dose_quantity ?? ''} ${it.dose_unit ?? ''}`}
                  </TableCell>
                  <TableCell>
                    {editable ? (
                      <Input value={it.route ?? ''} onChange={e => updateItem(idx, { route: e.target.value })} placeholder="Oral" className="w-20" />
                    ) : it.route}
                  </TableCell>
                  <TableCell>
                    {editable ? (
                      <div className="flex gap-1">
                        <NativeSelect value={it.frequency_code ?? ''} onChange={e => updateItem(idx, { frequency_code: e.target.value || null })} className="w-20">
                          <option value="">—</option>
                          {FREQUENCY_CODES.map(f => <option key={f} value={f}>{f}</option>)}
                        </NativeSelect>
                        <Input value={it.frequency_text ?? ''} onChange={e => updateItem(idx, { frequency_text: e.target.value })} placeholder="notes" className="w-24" />
                      </div>
                    ) : (it.frequency_text || it.frequency_code)}
                  </TableCell>
                  <TableCell>
                    {editable ? (
                      <div className="flex gap-1">
                        <Input type="number" value={it.duration_value ?? ''} onChange={e => updateItem(idx, { duration_value: e.target.value === '' ? null : Number(e.target.value) })} className="w-14" />
                        <NativeSelect value={it.duration_unit ?? 'd'} onChange={e => updateItem(idx, { duration_unit: e.target.value as PrescriptionDurationUnit })} className="w-16">
                          {DURATION_UNITS.map(u => <option key={u} value={u}>{u}</option>)}
                        </NativeSelect>
                      </div>
                    ) : `${it.duration_value ?? ''} ${it.duration_unit ?? ''}`}
                  </TableCell>
                  <TableCell>
                    {editable ? (
                      <Input
                        type="number"
                        min={1}
                        value={it.quantity ?? ''}
                        onChange={e => updateItem(idx, { quantity: e.target.value === '' ? null : Number(e.target.value) })}
                        placeholder="Auto"
                        className="w-16"
                        title="How many units to dispense — auto-suggested from dose × frequency × duration, editable"
                      />
                    ) : (it.quantity ?? '—')}
                  </TableCell>
                  <TableCell>
                    {editable ? (
                      <Checkbox checked={it.substitution_allowed !== false} onCheckedChange={c => updateItem(idx, { substitution_allowed: c === true })} />
                    ) : (it.substitution_allowed !== false ? 'Yes' : 'No')}
                  </TableCell>
                  {editable && (
                    <TableCell>
                      <Button size="sm" variant="ghost" onClick={() => removeItem(idx)}>✕</Button>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}

        {actionError && <div className="mt-2 text-[12px] text-danger">{actionError}</div>}

        {editable && (
          <div className="mt-3 flex gap-2">
            <Button size="sm" variant="ghost" loading={busy} onClick={save} disabled={!items.length}>Save draft</Button>
            <Button size="sm" variant="success" loading={busy} onClick={activate} disabled={!validItems.length || nothingToActivate}>
              {nothingToActivate ? '✓ Activated' : '✓ Activate prescription'}
            </Button>
          </div>
        )}

        {sheetLoading && (
          <div className="mt-3 flex items-center gap-2 rounded-[var(--radius)] border border-info/40 bg-info-soft px-3 py-2.5 text-[12.5px] font-medium text-info-ink">
            <span
              aria-hidden="true"
              className="h-4 w-4 shrink-0 animate-spin rounded-full border-2 border-current border-t-transparent"
            />
            📄 Generating the prescription sheet for your review — this can take a few seconds…
          </div>
        )}

        {sheetError && <div className="mt-2 text-[12px] text-danger">{sheetError}</div>}

        {sheetOpen && sheetDoc !== null && (
          <div className="mt-3 rounded-[var(--radius)] border border-border">
            <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border bg-muted/40 px-3 py-2">
              <span className="text-[12px] font-medium">
                📄 Prescription sheet — {sheetApproved
                  ? 'approved'
                  : sheetSource === 'template' ? 'built from the saved prescription, awaiting approval' : 'filled in with AI, awaiting approval'}
              </span>
              {!sheetApproved && (
                <div className="flex flex-wrap gap-2">
                  {aiEnabled && (
                    <Button size="sm" variant="ghost" loading={sheetAiFilling} onClick={fillSheetWithAi}
                      title="Fill Diagnosis, Advice and Follow-up from this visit's clinical data">
                      ✨ Fill with AI
                    </Button>
                  )}
                  <Button size="sm" variant="ghost" loading={sheetLoading} onClick={() => generateSheet()}>↻ Rebuild from prescription</Button>
                  <Button
                    size="sm"
                    variant="success"
                    disabled={sheetUnfilled.length > 0 || !sheetEditDoc.trim()}
                    title={sheetUnfilled.length ? `Fill in or remove: ${sheetUnfilled.join(', ')}` : undefined}
                    onClick={() => approveSheet()}
                  >
                    ✅ Approve & save
                  </Button>
                  <Button size="sm" variant="ghost" aria-label="Close the sheet" onClick={() => { setSheetOpen(false); setSheetDoc(null); setSheetMismatch(null); }}>✕</Button>
                </div>
              )}
              {sheetApproved && approvedSheet && (
                <DocumentPreviewButtons uhid={approvedSheet.uhid} docId={approvedSheet.id} label="Prescription sheet" />
              )}
            </div>
            {sheetUnfilled.length > 0 && !sheetApproved && (
              <div className="flex flex-wrap items-center gap-2 px-3 pt-2 text-[12px] text-warn-ink">
                {sheetUnfilled.length} field{sheetUnfilled.length === 1 ? '' : 's'} to fill: {sheetUnfilled.join(', ')}
                <Button size="sm" variant="link" onClick={() => setSheetEditDoc(removeAllPlaceholders(sheetEditDoc))}>Remove unfilled</Button>
              </div>
            )}
            {sheetMismatch && !sheetApproved && (
              <div className="mx-3 mt-2 rounded-[var(--radius)] border border-warn bg-warn-soft p-3 text-[12px] text-warn-ink">
                <div className="font-semibold">The medicines on this sheet differ from the saved prescription Pharmacy will dispense.</div>
                {sheetMismatch.missing.length > 0 && (
                  <div className="mt-1">
                    In the prescription but not on the sheet:
                    <ul className="ml-4 list-disc font-mono">{sheetMismatch.missing.map(line => <li key={`m-${line}`}>{line}</li>)}</ul>
                  </div>
                )}
                {sheetMismatch.unexpected.length > 0 && (
                  <div className="mt-1">
                    On the sheet but not in the prescription:
                    <ul className="ml-4 list-disc font-mono">{sheetMismatch.unexpected.map(line => <li key={`u-${line}`}>{line}</li>)}</ul>
                  </div>
                )}
                <div className="mt-2 flex flex-wrap gap-2">
                  <Button size="sm" variant="ghost" onClick={() => generateSheet()}>↻ Rebuild sheet from prescription</Button>
                  <Button size="sm" variant="danger" onClick={() => approveSheet(true)}>Save anyway</Button>
                </div>
              </div>
            )}
            {sheetSaveError && <div className="px-3 py-2 text-[12px] text-danger">⚠ Could not save: {sheetSaveError}</div>}
            {!sheetApproved && appliedSheetMemory.length > 0 && (
              <div className="px-3 pt-2">
                <PersonalisedChip applied={appliedSheetMemory} onOpenPreferences={() => setShowPreferences(true)} />
              </div>
            )}
            <div className="p-3">
              <DocumentEditor
                value={sheetEditDoc}
                onChange={(next) => {
                  setSheetEditDoc(next);
                  setSheetMismatch(current => (current && current.content !== next ? null : current));
                }}
                insertables={sheetInsertables}
                onRegenerateSection={regenerateSheetSection}
                readOnly={sheetApproved}
                ariaLabel="Prescription sheet"
                className="max-h-[460px] overflow-y-auto"
              />
              {!sheetApproved && (
                <div className="mt-2 text-[11px] text-ink-muted">
                  Changing medicines here doesn&apos;t change what Pharmacy dispenses — edit the prescription table above, save, then rebuild the sheet.
                </div>
              )}
            </div>
            {sheetApproved && proposal && (
              <div className="border-t border-border p-3">
                <ProposalCard proposal={proposal} onDone={() => setProposal(null)} />
              </div>
            )}
          </div>
        )}
      </CardContent>
      <MyAiPreferencesPanel open={showPreferences} onOpenChange={setShowPreferences} />
    </Card>
  );
}
